import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Text, useThemeUI } from "theme-ui";

import castArray from "lodash/castArray";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import isString from "lodash/fp/isString";
import map from "lodash/fp/map";
import update from "lodash/fp/update";

import { useTranslation } from "hooks";
import { Button, Input, LoadingIndicator } from "components";
import { CheckmarkIcon } from "components/icons";
import ModalCenter from "../ModalCenter";
import * as styles from "./ModalLeaveApp.styles";
import {
  createImage,
  saveCurrentProgress,
  marketingEmailSubscription,
} from "api";
import {
  selectUserChoiceValues,
  selectSelectedProduct,
  selectProductId,
  selectProductImages,
  selectUserChoicesForUi,
  selectProductCost,
} from "store/app";
import { formatExternalLink, getEmailPlaceholderImages } from "utils";

import { Checkbox } from "components/Forms";

export default function ModalLeaveApp({
  isTopMostModal,
  title,
  onClose,
  leaveYourCustomDesignObj,
  emailImages,
}) {
  const { t } = useTranslation();
  const {
    theme: { isMobile },
  } = useThemeUI();

  const [value, setValue] = useState("");
  const [abortControllerID, setAbortControllerID] = useState(0);
  const [abortController, setAbortController] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const selectedProduct = useSelector(selectSelectedProduct);
  const cost = useSelector(selectProductCost);
  const userChoiceValues = useSelector(selectUserChoiceValues);
  const userChoiceValuesForUI = useSelector(selectUserChoicesForUi(t));
  const id = useSelector(selectProductId);
  const locale = useSelector((state) => state.app.locale);
  const productImages = useSelector(selectProductImages);
  const [checked, setChecked] = useState(true);

  const images = productImages.map(
    update("images", flow(map(get("url")), filter(isString)))
  );

  const imagesArray = castArray(images);

  const imagesToFlatten = emailImages
    ? getEmailPlaceholderImages(emailImages, userChoiceValues)
    : imagesArray;

  const handleChange = () => {
    setChecked(!checked);
  };

  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortControllerID]);

  const handleSubmit = (evt) => {
    evt.preventDefault();
    const controller = new AbortController();
    setAbortController(controller);
    setAbortControllerID(abortControllerID + 1);
    setIsLoading(true);

    Promise.all([
      ...imagesToFlatten.map(({ imageSet, images }) =>
        createImage({ signal: controller.signal })({ images }).then(
          (finalImage) => ({ imageSet, finalImage })
        )
      ),
    ])
      .then((finalImages) => {
        return saveCurrentProgress({ signal: controller.signal })({
          value,
          selectedProduct,
          final_image: finalImages,
          userChoiceValuesForUI,
          cost,
          id,
          locale: locale.split("-")[0],
        });
      })
      .then(() => {
        setSaveSuccess(true);
        setIsLoading(false);
        console.log("share working", locale);
        if (checked) {
          if (locale === "en-CA") {
            return marketingEmailSubscription()({
              url: process.env.REACT_APP_MARKETING_URL_CA,
              list_id: process.env.REACT_APP_KLAVIYO_LIST_ID_CA,
              custom_source: "mybauer_abandoned",
              email: value,
            });
          } else {
            return marketingEmailSubscription()({
              url: process.env.REACT_APP_MARKETING_URL_US,
              list_id: process.env.REACT_APP_KLAVIYO_LIST_ID_US,
              custom_source: "mybauer_abandoned",
              email: value,
            });
          }
        }
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  return (
    <ModalCenter isTopMostModal={isTopMostModal} onClose={onClose}>
      <Box as="form" onSubmit={handleSubmit} sx={styles.contentContainer}>
        {isLoading && (
          <Box sx={styles.loadingIndicatorContainer}>
            <LoadingIndicator />
          </Box>
        )}
        <Text variant="h4Light" sx={styles.modalTitle}>
          {!saveSuccess
            ? title
            : leaveYourCustomDesignObj?.successTitle || title}
        </Text>

        <Text as="div" variant="copy3Light" sx={styles.descriptionText}>
          {leaveYourCustomDesignObj.textPart1}
        </Text>
        {saveSuccess && (
          <>
            <Text as="div" variant="copy2Medium" sx={styles.descriptionText}>
              <Box sx={styles.checkmarkIcon}>
                <CheckmarkIcon />
              </Box>
              {leaveYourCustomDesignObj.saveConfirmation}
            </Text>
            <Text as="div" variant="copy2Medium" sx={styles.subText}>
              {value}
            </Text>
          </>
        )}
        {!saveSuccess && (
          <>
            <Input
              sx={styles.emailInput}
              variant="round"
              placeholder={
                leaveYourCustomDesignObj.inputPlaceholder ||
                "recipient@domain.com"
              }
              onChange={setValue}
              value={value}
            />

            <Text as="div" variant="copy3Light" sx={styles.descriptionText}>
              {leaveYourCustomDesignObj.textPart2}
            </Text>
            <Text as="div" variant="copy3Light" sx={styles.descriptionText}>
              {leaveYourCustomDesignObj.confirmText}
            </Text>
            <Box sx={styles.marketingBox}>
              <Box sx={styles.marketingCheckbox}>
                <Checkbox
                  size={isMobile ? "small" : "medium"}
                  value={locale === "en-CA" ? !checked : checked}
                  onChange={handleChange}
                  defaultValue
                />
              </Box>
              <Text
                as="div"
                variant="copy2Medium"
                sx={styles.marketingSubTitle}
              >
                {leaveYourCustomDesignObj.marketingSubTitle}
              </Text>
            </Box>

            <Box sx={styles.buttonsContainer}>
              <Button
                variant="secondary"
                size="medium"
                isRound
                href={formatExternalLink(leaveYourCustomDesignObj.link)}
                rel="noopener noreferrer"
              >
                {t("close")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="medium"
                isRound
                theme="dark"
                disabled={!Boolean(value)}
              >
                {t("confirm")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </ModalCenter>
  );
}
